import React, { useState } from "react"
import { TZDate, type DateRange } from "react-day-picker"

import {
  Calendar as CalendarGrid,
  cn,
  Input,
  Tooltip,
  useDateRangeCalendarContext,
} from "@coval/ui"
import { endOfDay, format, isSameDay, set, startOfDay } from "date-fns"

import { applyDateOnlyInput } from "@/utils/date/applyDateOnlyInput"

interface DateTimeRangePickerProps {
  dateOnly?: boolean
  startDate: Date | null
  endDate: Date | null
  onChange: (start: Date | null, end: Date | null) => void
  isLoading?: boolean
  className?: string
  /** Hide the "(Local)" timezone hint next to the time fields. */
  hideTimezoneHint?: boolean
}

interface DateTimePickerProps {
  value: Date | null
  onChange: (value: Date | null) => void
  isLoading?: boolean
  className?: string
  hideTimezoneHint?: boolean
  /**
   * The latest instant the field accepts. Later days leave the calendar, the
   * time field stops at it on the day it falls in, and any bound that would
   * still land past it is pulled back, so no entry path produces a later value.
   */
  maxDate?: Date
}

/**
 * Keeps the time already on a bound when the calendar moves its day, so
 * picking a new day never silently widens or narrows a window. Seconds and
 * milliseconds come along too: presets seed ends at 23:59:59.999, and dropping
 * that tail would quietly cut the last minute out of the range.
 */
function applyDayToBound(
  day: Date,
  bound: Date | null,
  fallback: (date: Date) => Date,
  timeZone?: string,
) {
  const calendarDay = inTimeZone(day, timeZone)
  if (!bound) {
    return fallback(calendarDay)
  }
  const calendarBound = inTimeZone(bound, timeZone)
  if (isSameDay(calendarDay, calendarBound)) {
    return bound
  }
  return set(calendarDay, {
    hours: calendarBound.getHours(),
    minutes: calendarBound.getMinutes(),
    seconds: calendarBound.getSeconds(),
    milliseconds: calendarBound.getMilliseconds(),
  })
}

function inTimeZone(date: Date, timeZone?: string) {
  return timeZone ? new TZDate(date, timeZone) : date
}

function changedBound(date: Date | null, original: Date | null) {
  return date && date !== original ? new Date(date.getTime()) : date
}

function formatTimeForInput(date: Date | null) {
  return date ? format(date, "HH:mm") : ""
}

const INVERTED_RANGE_MESSAGE = "End time must be after the start time."

/**
 * A time field reports an empty value while it is half-typed, so only a
 * complete time is worth pushing onto a bound.
 */
const COMPLETE_TIME = /^\d{2}:\d{2}$/

function applyTimeToBound(bound: Date, time: string, timeZone?: string) {
  const [hours, minutes] = time.split(":").map(Number)
  return set(inTimeZone(bound, timeZone), {
    hours,
    minutes,
    seconds: 0,
    milliseconds: 0,
  })
}

/**
 * Picks a date range: the calendar owns the days, the time fields refine the
 * hours. A bound with no time runs the whole day, which is what the presets
 * produce, so time entry stays optional on surfaces that never need it. The
 * end always lands strictly after the start, so an applied range spans time.
 */
export const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  startDate,
  endDate,
  dateOnly = false,
  onChange,
  isLoading = false,
  className = "",
  hideTimezoneHint = false,
}) => {
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const calendarTimeZone = useDateRangeCalendarContext()?.timeZone
  const timeZone = calendarTimeZone ?? localTimeZone
  const calendarStart = startDate
    ? inTimeZone(startDate, calendarTimeZone)
    : null
  const calendarEnd = endDate ? inTimeZone(endDate, calendarTimeZone) : null
  const [rangeError, setRangeError] = useState<string | null>(null)
  /** Only a same-day range can be inverted by a time: the calendar orders days. */
  const sharesOneDay = Boolean(
    calendarStart && calendarEnd && isSameDay(calendarStart, calendarEnd),
  )

  function handleRangeSelect(range: DateRange | undefined) {
    setRangeError(null)
    let nextStart = range?.from
      ? applyDayToBound(range.from, startDate, startOfDay, calendarTimeZone)
      : null
    let nextEnd = range?.to
      ? applyDayToBound(range.to, endDate, endOfDay, calendarTimeZone)
      : null
    /**
     * Carried times can collapse a multi-day range onto one day, leaving an
     * end at or before the start. A day picked that way runs whole, matching
     * what a day picked with no times on its bounds produces.
     */
    if (nextStart && nextEnd && nextEnd <= nextStart) {
      nextStart = startOfDay(inTimeZone(nextStart, calendarTimeZone))
      nextEnd = endOfDay(inTimeZone(nextEnd, calendarTimeZone))
    }
    onChange(changedBound(nextStart, startDate), changedBound(nextEnd, endDate))
  }

  function handleStartTimeChange(time: string) {
    if (!startDate) {
      return
    }
    const nextStart = applyTimeToBound(startDate, time, calendarTimeZone)
    if (endDate && nextStart >= endDate) {
      setRangeError(INVERTED_RANGE_MESSAGE)
      return
    }
    setRangeError(null)
    onChange(changedBound(nextStart, startDate), endDate)
  }

  function handleEndTimeChange(time: string) {
    if (!endDate) {
      return
    }
    const nextEnd = applyTimeToBound(endDate, time, calendarTimeZone)
    if (startDate && nextEnd <= startDate) {
      setRangeError(INVERTED_RANGE_MESSAGE)
      return
    }
    setRangeError(null)
    onChange(startDate, changedBound(nextEnd, endDate))
  }

  return (
    <div className="flex flex-col gap-3">
      <CalendarGrid
        mode="range"
        selected={
          startDate || endDate
            ? { from: startDate ?? undefined, to: endDate ?? undefined }
            : undefined
        }
        onSelect={handleRangeSelect}
        defaultMonth={startDate ?? endDate ?? undefined}
        disabled={isLoading}
        className="self-center p-0"
      />
      <div className={cn("flex flex-col gap-2", className)}>
        {dateOnly ? (
          <>
            <Input
              type="date"
              aria-label="Start date"
              value={calendarStart ? format(calendarStart, "yyyy-MM-dd") : ""}
              disabled={isLoading}
              onChange={(event) =>
                onChange(
                  applyDateOnlyInput(
                    event.target.value,
                    startDate,
                    startOfDay,
                    calendarTimeZone,
                  ),
                  endDate,
                )
              }
            />
            <Input
              type="date"
              aria-label="End date"
              value={calendarEnd ? format(calendarEnd, "yyyy-MM-dd") : ""}
              disabled={isLoading}
              onChange={(event) =>
                onChange(
                  startDate,
                  applyDateOnlyInput(
                    event.target.value,
                    endDate,
                    endOfDay,
                    calendarTimeZone,
                  ),
                )
              }
            />
          </>
        ) : (
          <>
            <BoundRow
              label="Start"
              date={calendarStart}
              isLoading={isLoading}
              max={sharesOneDay ? formatTimeForInput(calendarEnd) : undefined}
              onTimeChange={handleStartTimeChange}
            />
            <BoundRow
              label="End"
              date={calendarEnd}
              isLoading={isLoading}
              min={sharesOneDay ? formatTimeForInput(calendarStart) : undefined}
              onTimeChange={handleEndTimeChange}
            />
          </>
        )}
        {rangeError && (
          <p className="text-xs text-coval-danger-text">{rangeError}</p>
        )}
        {!dateOnly && !hideTimezoneHint && (
          <Tooltip content={timeZone}>
            <span className="self-end text-xs text-coval-text-muted">
              Times are{" "}
              {timeZone === localTimeZone ? "local" : `in ${timeZone}`}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  isLoading = false,
  className = "",
  hideTimezoneHint = false,
  maxDate,
}) => {
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const calendarTimeZone = useDateRangeCalendarContext()?.timeZone
  const timeZone = calendarTimeZone ?? localTimeZone
  const calendarValue = value ? inTimeZone(value, calendarTimeZone) : null
  const calendarMax = maxDate
    ? inTimeZone(maxDate, calendarTimeZone)
    : undefined
  const disabledDays = maxDate ? { after: maxDate } : undefined
  /**
   * A time field only marks an out-of-range value invalid, so the bound is
   * clamped here as well: typing past the maximum lands on it instead.
   */
  const clampToMax = (next: Date | null) =>
    next && maxDate && next > maxDate ? maxDate : next

  function handleDaySelect(day: Date | undefined) {
    const next = clampToMax(
      day ? applyDayToBound(day, value, startOfDay, calendarTimeZone) : null,
    )
    onChange(changedBound(next, value))
  }

  function handleTimeChange(time: string) {
    if (!value) {
      return
    }
    onChange(
      changedBound(
        clampToMax(applyTimeToBound(value, time, calendarTimeZone)),
        value,
      ),
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <CalendarGrid
        mode="single"
        selected={value ?? undefined}
        onSelect={handleDaySelect}
        defaultMonth={value ?? undefined}
        disabled={isLoading || disabledDays}
        className="self-center p-0"
      />
      <div className={cn("flex flex-col gap-2", className)}>
        <BoundRow
          label="Time"
          date={calendarValue}
          isLoading={isLoading}
          max={
            calendarMax &&
            calendarValue &&
            isSameDay(calendarValue, calendarMax)
              ? formatTimeForInput(calendarMax)
              : undefined
          }
          timeAriaLabel="Time"
          onTimeChange={handleTimeChange}
        />
        {!hideTimezoneHint && (
          <Tooltip content={timeZone}>
            <span className="self-end text-xs text-coval-text-muted">
              Times are{" "}
              {timeZone === localTimeZone ? "local" : `in ${timeZone}`}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

/**
 * One bound of the range: the day the calendar picked, and the time field that
 * refines it. Without a day there is nothing to refine, so the field waits.
 *
 * The field holds what the user is typing rather than the bound's time, so an
 * emptied or half-entered value survives instead of snapping back mid-edit;
 * only a complete time reaches the bound, and an abandoned edit is restored on
 * blur so the field never disagrees with the bound it labels.
 */
function BoundRow({
  date,
  isLoading,
  label,
  max,
  min,
  onTimeChange,
  timeAriaLabel,
}: {
  date: Date | null
  isLoading: boolean
  label: string
  max?: string
  min?: string
  onTimeChange: (time: string) => void
  timeAriaLabel?: string
}) {
  const time = formatTimeForInput(date)
  const [draft, setDraft] = useState(time)
  const [appliedTime, setAppliedTime] = useState(time)

  if (time !== appliedTime) {
    setAppliedTime(time)
    setDraft(time)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDraft(e.target.value)
    if (COMPLETE_TIME.test(e.target.value)) {
      onTimeChange(e.target.value)
    }
  }

  function handleBlur() {
    if (!COMPLETE_TIME.test(draft)) {
      setDraft(time)
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 flex-col">
        <span className="text-xs text-coval-text-muted">{label}</span>
        <span className="truncate text-sm text-coval-text">
          {date ? format(date, "MMM d, yyyy") : "Pick a date"}
        </span>
      </div>
      <Input
        type="time"
        value={draft}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isLoading || !date}
        min={min}
        max={max}
        className="w-32 shrink-0"
        aria-label={timeAriaLabel ?? `${label} time`}
      />
    </div>
  )
}
